/**
 * @module useApp
 * App-wide singleton state: authentication status, current app context,
 * navigation drawer, and cached lookup options.
 *
 * `currentApp` tracks which microservice the user is currently using
 * ("sales", "marketing", "assets", etc.). It drives which navigation
 * items are shown and which API calls are made. Stored in localStorage so
 * it survives page refresh.
 *
 * Options (cities, locations, brands, platforms) are cached in localStorage
 * so the UI renders immediately on load with the last-known values, then
 * refreshes from the API in the background. This stale-while-revalidate
 * pattern prevents visible loading states on navigation.
 */

import { ref, computed, reactive } from "vue";
import api from "@/utils/api.js";
import { parseGrants, holdsApp, canEditApp as canEditAppFor, canReadPayroll as canReadPayrollFor } from "@/utils/appGrants.js";
// The apps of the page on screen (its route's meta.app), set by the router.
import { routeApps, setRouteApps } from "@/utils/routeApps.js";

const isDev = import.meta.env.MODE === "development";

// ── Navigation state ──────────────────────────────────────────────────────────

const drawer = ref(false);

// Speed-dial menus in the app bar
const showMainMenu = ref(false);
const showReviewsMenu = ref(false);
const showMenuMenu = ref(false);
const showContactsMenu = ref(false);
const showWarehouseMenu = ref(false);
const showMealsMenu = ref(false);
const showDisputesMenu = ref(false);
const showJournalMenu = ref(false);
const showTasksMenu = ref(false);

// ── Auth state ────────────────────────────────────────────────────────────────

const isAuthenticated = ref(false);
const userRole = ref("user");

const token = localStorage.getItem("authToken");
const role = localStorage.getItem("userRole");
const user = ref(JSON.parse(localStorage.getItem("user")));

// The admin-tier ROLE (admin or appadmin), whatever page is on screen.
const isAdminRole = computed(() => ["admin", "appadmin"].includes(userRole.value));

// allowed_apps is JSONB — a native array, or a string on pre-migration rows.
// Raw grants: an app code, or "<code>:view" for a view-only grant (2026-11-04).
const allowedApps = computed(() => {
  let a = user.value?.allowed_apps;
  if (!a) return [];
  if (typeof a === "string") { try { a = JSON.parse(a); } catch { return []; } }
  return Array.isArray(a) ? a : [];
});

// The person as the grant rules (utils/appGrants.js) see them.
const grantUser = computed(() => ({ role: userRole.value, allowed_apps: allowedApps.value }));

/**
 * The app codes held, to change or only to look ("gl:view" → "gl") — what the
 * sidebar, Home and other "is this module visible?" checks match against.
 */
const appCodes = computed(() => {
  const { full, view } = parseGrants(allowedApps.value);
  return [...full, ...view];
});

/**
 * Does this user hold one of these app grants — to change or only to look?
 * appadmin always does. Mirrors requireAppAccess() on a read on the server —
 * the UI hides what the API would refuse anyway, so the two must agree.
 */
const hasApp = (...codes) => holdsApp(grantUser.value, codes);

/** May this user CHANGE data in one of these apps? Admin role + a full (not view-only) grant. */
const canEditApp = (...codes) => canEditAppFor(grantUser.value, codes);

/** May this user see salaries (Payroll, staff provisions)? Mirrors lib/payroll-access.js. */
const canReadPayroll = computed(() => canReadPayrollFor(grantUser.value));

/**
 * May this person change things ON THE PAGE ON SCREEN? The admin-tier role,
 * and — when the page belongs to an app — a full grant for it. A view-only
 * grant ("gl:view") turns every page of that app read-only, so the edit
 * buttons pages show behind `isAdmin` hide by themselves (2026-11-04; until
 * then this was the role alone). For a check about a SPECIFIC app, whatever
 * the page, use canEditApp(...codes); for the raw role, isAdminRole.
 */
const isAdmin = computed(() => {
  if (!isAdminRole.value) return false;
  const apps = routeApps.value;
  return !apps || apps.length === 0 || canEditApp(...apps);
});

if (token) {
  isAuthenticated.value = true;
  userRole.value = role || "user";
}

// ── Current app context ───────────────────────────────────────────────────────

const currentApp = ref(JSON.parse(localStorage.getItem("currentApp")));
const isSales = computed(() => currentApp.value === "sales");
const isReviews = computed(() => currentApp.value === "reviews");

// ── Lookup options ────────────────────────────────────────────────────────────

const options = reactive({
  countries: [{ name: "UAE" }],
  cities: [{ name: "Dubai" }, { name: "Al Ain" }],
  locations: [],
  brands: [],
  platforms: [],
});

/**
 * Load lookup options with stale-while-revalidate:
 * 1. Immediately restore cached options so the UI isn't empty
 * 2. Fetch fresh options from the API in the background
 * 3. Update the cache with the fresh data
 *
 * This means selects are populated instantly on page load, and any new
 * locations/brands added in Settings appear after the next fetch.
 */
const fetchOptions = async () => {
  const storedOptions = JSON.parse(localStorage.getItem("options"));
  if (storedOptions) {
    Object.assign(options, storedOptions);
  }
  try {
    const response = await api.accountingApi.get("/api/accounting/sales/options");
    Object.assign(options, response.data);
    localStorage.setItem("options", JSON.stringify(options));
  } catch (err) {
    console.error("Failed to fetch options:", err);
  }
};

// ── App metadata ──────────────────────────────────────────────────────────────

const title = "Pizza Felice LLC";
const salesTitle = "Sales";
const reviewsTitle = "Reviews";

export function useApp() {
  return {
    currentApp,
    isSales,
    isReviews,
    drawer,
    fetchOptions,
    options,
    isDev,
    user,
    title,
    salesTitle,
    reviewsTitle,
    isAdmin,
    isAdminRole,
    allowedApps,
    appCodes,
    grantUser,
    hasApp,
    canEditApp,
    canReadPayroll,
    routeApps,
    setRouteApps,
    isAuthenticated,
    userRole,
    showMainMenu,
    showReviewsMenu,
    showMenuMenu,
    showContactsMenu,
    showWarehouseMenu,
    showMealsMenu,
    showDisputesMenu,
    showJournalMenu,
    showTasksMenu,
  };
}
